import EmailIcon from "@mui/icons-material/Email";
import InfoIcon from "@mui/icons-material/Info";
import SmsIcon from "@mui/icons-material/Sms";
import WarningIcon from "@mui/icons-material/Warning";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  List,
  ListItem,
  ListItemText,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";

import type { Section12DoctorSearchResult } from "@thalamos/aspire-common";

interface ContactDoctorsConfirmDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  isSubmitting: boolean;
  doctors: Section12DoctorSearchResult[];
  /** Doctors that were selected but excluded because they have already been contacted */
  alreadyContactedDoctors?: Section12DoctorSearchResult[];
}

export function ContactDoctorsConfirmDialog({
  open,
  onClose,
  onConfirm,
  isSubmitting,
  doctors,
  alreadyContactedDoctors = [],
}: ContactDoctorsConfirmDialogProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  return (
    <Dialog
      open={open}
      onClose={isSubmitting ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      fullScreen={isMobile}
      data-testid="contact-doctors-confirm-dialog"
    >
      <DialogTitle>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <WarningIcon color="warning" />
          <Typography variant="h6">
            Contact {doctors.length} Doctor{doctors.length !== 1 ? "s" : ""}?
          </Typography>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        <Typography variant="body1" sx={{ mb: 2 }}>
          You are about to send invitations to the following doctor
          {doctors.length !== 1 ? "s" : ""}:
        </Typography>
        <List dense sx={{ py: 0 }}>
          {doctors.map((doctor) => (
            <ListItem
              key={doctor.id}
              sx={{
                py: 0.5,
                alignItems: "flex-start",
                flexDirection: { xs: "column", sm: "row" },
                gap: { xs: 0.5, sm: 0 },
              }}
            >
              <ListItemText
                primary={doctor.name}
                secondary={doctor.email}
                slotProps={{
                  primary: { variant: "body2" },
                  secondary: { variant: "caption" },
                }}
                sx={{ minWidth: 0, mb: 0 }}
              />
              <Chip
                size="small"
                icon={doctor.phoneNumber ? <SmsIcon /> : <EmailIcon />}
                label={doctor.phoneNumber ? "Email + SMS" : "Email only"}
                variant="outlined"
                color={doctor.phoneNumber ? "success" : "default"}
                sx={{ flexShrink: 0, mt: { xs: 0, sm: 0.25 } }}
              />
            </ListItem>
          ))}
        </List>

        <Divider sx={{ my: 2 }} />

        {alreadyContactedDoctors.length > 0 && (
          <Alert
            severity="info"
            icon={<InfoIcon />}
            sx={{ mb: 2 }}
            data-testid="already-contacted-alert"
          >
            {alreadyContactedDoctors.length} doctor
            {alreadyContactedDoctors.length !== 1 ? "s have" : " has"} already
            been contacted and will not be re-contacted:{" "}
            {alreadyContactedDoctors.map((d) => d.name).join(", ")}.
          </Alert>
        )}

        <Typography
          variant="body2"
          sx={{
            color: "text.secondary",
          }}
        >
          Doctors with verified phone numbers will receive both an email and an
          SMS notification. Others will receive email only.
        </Typography>
      </DialogContent>
      <DialogActions
        sx={{
          px: 3,
          py: 2,
          flexDirection: { xs: "column", sm: "row" },
          gap: { xs: 1, sm: 0 },
        }}
      >
        <Button
          variant="contained"
          onClick={onConfirm}
          disabled={isSubmitting}
          startIcon={
            isSubmitting ? <CircularProgress size={20} /> : <EmailIcon />
          }
          data-testid="confirm-contact-doctors-button"
          sx={{ width: { xs: "100%", sm: "auto" } }}
        >
          {isSubmitting ? "Sending..." : "Send Invitations"}
        </Button>
        <Button
          onClick={onClose}
          disabled={isSubmitting}
          sx={{ width: { xs: "100%", sm: "auto" } }}
        >
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
}
